import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  FileManagerContextType,
  FileState,
  PerformActionPayload,
  FileNode,
  ActionType,
} from "./types";
import { fileReducer, initialState } from "./reducer";
import * as api from "./api";
import { useToast } from "../../contexts/ToastContext";
import { useOrg } from "../../contexts/OrgContext";

const FileManagerContext = createContext<FileManagerContextType | null>(null);

/**
 * Get user-friendly error messages for each action type
 */
function getErrorMessage(error: unknown, actionType: ActionType): string {
  const errorMessage =
    error instanceof Error ? error.message : "An error occurred";

  const actionLabels: Record<ActionType, string> = {
    move: "Move",
    rename: "Rename",
    delete: "Delete",
    createFolder: "Create folder",
    upload: "Upload",
    listFolder: "Load folder",
    download: "Download",
    toggleExpand: "Expand folder",
    select: "Select",
    deselect: "Deselect",
    multiSelect: "Select items",
    setViewMode: "Change view",
    sort: "Sort",
  };

  return `${actionLabels[actionType]} failed: ${errorMessage}`;
}

export interface FileManagerProviderProps {
  children: React.ReactNode;
  initialNodes?: Record<string, FileNode>;
  caseId?: string; // Optional case ID for backend integration
}

export function FileManagerProvider({
  children,
  initialNodes,
  caseId,
}: FileManagerProviderProps) {
  const { showToast } = useToast();
  const { orgId } = useOrg();
  const [state, dispatch] = useReducer(fileReducer, {
    ...initialState,
    nodes: initialNodes || {},
    rootIds: initialNodes
      ? Object.values(initialNodes)
          .filter((node) => node.parentId === null)
          .map((node) => node.id)
      : [],
  });

  /**
   * Main action dispatcher - handles all file operations with optimistic updates
   */
  const performAction = useCallback(
    async (payload: PerformActionPayload): Promise<void> => {
      const opId = uuidv4();

      // Create snapshot for rollback
      const snapshot: Partial<FileState> = {
        nodes: { ...state.nodes },
        rootIds: [...state.rootIds],
        selectedIds: new Set(state.selectedIds),
      };

      try {
        dispatch({
          type: "OP_START",
          payload: { opId, actionType: payload.type },
        });

        switch (payload.type) {
          case "move": {
            if (!payload.nodeId && !payload.nodeIds) {
              throw new Error("nodeId or nodeIds required for move");
            }

            const nodeIds = payload.nodeIds || [payload.nodeId!];

            // Optimistic update
            dispatch({
              type: "MOVE_OPTIMISTIC",
              payload: {
                nodeIds,
                targetParentId: payload.targetParentId || null,
              },
            });

            // API call
            if (nodeIds.length === 1) {
              const updated = await api.apiMove(
                state.nodes[nodeIds[0]],
                payload.targetParentId || null,
              );
              dispatch({
                type: "MOVE_SUCCESS",
                payload: { nodes: [updated] },
              });
            } else {
              const updated = await api.apiBatchMove(
                Object.entries(state.nodes).reduce((accu, [id, node]) => {
                  if (nodeIds.includes(id)) {
                    return [...accu, node];
                  }
                  return accu;
                }, [] as FileNode[]),
                payload.targetParentId || null,
              );
              dispatch({
                type: "MOVE_SUCCESS",
                payload: { nodes: updated },
              });
            }

            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({
              nodeIds,
              targetParentId: payload.targetParentId,
            });
            break;
          }

          case "rename": {
            if (!payload.nodeId || !payload.newName) {
              throw new Error("nodeId and newName required for rename");
            }

            // Optimistic update
            dispatch({
              type: "RENAME_OPTIMISTIC",
              payload: { nodeId: payload.nodeId, newName: payload.newName },
            });

            // API call
            const updated = await api.apiRename(
              payload.nodeId,
              payload.newName,
            );

            dispatch({
              type: "RENAME_SUCCESS",
              payload: { node: updated },
            });

            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.(updated);
            break;
          }

          case "delete": {
            if (!payload.nodeId && !payload.nodeIds) {
              throw new Error("nodeId or nodeIds required for delete");
            }

            const nodeIds = payload.nodeIds || [payload.nodeId!];

            // Optimistic update
            dispatch({
              type: "DELETE_OPTIMISTIC",
              payload: { nodeIds },
            });

            // API call
            if (nodeIds.length === 1) {
              const node = state.nodes[nodeIds[0]];
              await api.apiDelete(nodeIds[0], node?.type);
            } else {
              await api.apiBatchDelete(
                Object.values(state.nodes).map((node) => ({
                  id: node.id,
                  type: node.type,
                })),
              );
            }

            dispatch({
              type: "DELETE_SUCCESS",
              payload: { nodeIds },
            });

            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ nodeIds });
            break;
          }

          case "createFolder": {
            if (!payload.folderName) {
              throw new Error("folderName required for createFolder");
            }

            // Create temporary node for optimistic update
            const tempId = `temp-${uuidv4()}`;
            const tempNode: FileNode = {
              id: tempId,
              name: payload.folderName,
              type: "folder",
              parentId: payload.targetParentId || null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

            dispatch({
              type: "CREATE_FOLDER_OPTIMISTIC",
              payload: { tempNode },
            });

            // API call
            const created = await api.apiCreateFolder(
              payload.targetParentId || null,
              payload.folderName,
              caseId,
              payload.clientPermission,
            );

            dispatch({
              type: "CREATE_FOLDER_SUCCESS",
              payload: { tempId, node: created },
            });

            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.(created);
            break;
          }

          case "upload": {
            if (!payload.file && !payload.files) {
              throw new Error("file or files required for upload");
            }

            const files = payload.files || [payload.file!];
            const uploadedFiles: FileNode[] = [];

            // Process each file
            for (const file of files) {
              const fileId = uuidv4();

              // Track upload progress
              dispatch({
                type: "SET_UPLOAD_PROGRESS",
                payload: {
                  fileId,
                  fileName: file.name,
                  progress: 0,
                  status: "uploading",
                },
              });

              try {
                const onProgress = (pct: number) => {
                  dispatch({
                    type: "SET_UPLOAD_PROGRESS",
                    payload: {
                      fileId,
                      fileName: file.name,
                      progress: pct,
                      status: "uploading",
                    },
                  });
                };

                try {
                  const created = await api.apiUploadFile(
                    payload.targetParentId || state.activeFolderId,
                    file,
                    onProgress,
                    caseId,
                    orgId,
                  );

                  // Add node to state
                  dispatch({ type: "ADD_NODE", payload: created });
                  uploadedFiles.push(created);
                } catch (err) {
                  if (err instanceof Error) {
                    showToast(err.message);
                  }
                }

                // Mark upload complete
                dispatch({
                  type: "SET_UPLOAD_PROGRESS",
                  payload: {
                    fileId,
                    fileName: file.name,
                    progress: 100,
                    status: "complete",
                  },
                });

                // Remove from upload tracking after a delay
                setTimeout(() => {
                  dispatch({ type: "REMOVE_UPLOAD", payload: fileId });
                }, 2000);
              } catch (error) {
                console.error(`Upload failed for file ${file.name}:`, error);
                dispatch({
                  type: "SET_UPLOAD_PROGRESS",
                  payload: {
                    fileId,
                    fileName: file.name,
                    progress: 0,
                    status: "error",
                    error:
                      error instanceof Error ? error.message : "Upload failed",
                  },
                });
                // Re-throw error if this was a single file upload
                if (payload.file) {
                  throw error;
                }
              }
            }

            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            // For single file upload, pass the file node directly
            // For multiple files, pass the array
            if (uploadedFiles.length > 0) {
              if (payload.file) {
                payload.onSuccess?.(uploadedFiles[0]);
              } else {
                payload.onSuccess?.({
                  files: uploadedFiles,
                  count: uploadedFiles.length,
                });
              }
            }
            break;
          }

          case "listFolder": {
            dispatch({ type: "SET_LOADING", payload: true });

            const folderId = payload.nodeId || state.activeFolderId || "root";
            const nodes = await api.apiListFolder(folderId, caseId);

            // Add/update nodes
            nodes.forEach((node) => {
              if (state.nodes[node.id]) {
                dispatch({
                  type: "UPDATE_NODE",
                  payload: { id: node.id, updates: node },
                });
              } else {
                dispatch({ type: "ADD_NODE", payload: node });
              }
            });

            dispatch({ type: "SET_LOADING", payload: false });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.(nodes);
            break;
          }

          case "toggleExpand": {
            if (!payload.nodeId) {
              throw new Error("nodeId required for toggleExpand");
            }

            dispatch({ type: "TOGGLE_EXPAND", payload: payload.nodeId });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ nodeId: payload.nodeId });
            break;
          }

          case "select": {
            if (!payload.nodeId) {
              throw new Error("nodeId required for select");
            }

            dispatch({ type: "SELECT_NODE", payload: payload.nodeId });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ nodeId: payload.nodeId });
            break;
          }

          case "deselect": {
            if (!payload.nodeId) {
              throw new Error("nodeId required for deselect");
            }

            dispatch({ type: "DESELECT_NODE", payload: payload.nodeId });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ nodeId: payload.nodeId });
            break;
          }

          case "multiSelect": {
            if (!payload.nodeIds) {
              throw new Error("nodeIds required for multiSelect");
            }

            dispatch({ type: "SET_SELECTED", payload: payload.nodeIds });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ nodeIds: payload.nodeIds });
            break;
          }

          case "setViewMode": {
            if (!payload.viewMode) {
              throw new Error("viewMode required for setViewMode");
            }

            dispatch({ type: "SET_VIEW_MODE", payload: payload.viewMode });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ viewMode: payload.viewMode });
            break;
          }

          case "sort": {
            if (!payload.sortKey || !payload.sortDir) {
              throw new Error("sortKey and sortDir required for sort");
            }

            dispatch({
              type: "SET_SORT",
              payload: { key: payload.sortKey, dir: payload.sortDir },
            });
            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({
              sortBy: { key: payload.sortKey, dir: payload.sortDir },
            });
            break;
          }

          case "download": {
            if (!payload.nodeId) {
              throw new Error("nodeId required for download");
            }

            const node = state.nodes[payload.nodeId];
            if (!node) {
              throw new Error("Node not found");
            }

            // Download is now handled directly by the API
            await api.apiDownloadFile(payload.nodeId, node.name);

            dispatch({ type: "OP_SUCCESS", payload: { opId } });
            payload.onSuccess?.({ nodeId: payload.nodeId });
            break;
          }

          default:
            throw new Error(`Unknown action type: ${payload.type}`);
        }
      } catch (error) {
        // Rollback on error
        dispatch({
          type: "OP_FAILURE",
          payload: {
            opId,
            error: error instanceof Error ? error.message : "Operation failed",
            snapshot,
          },
        });

        // Call custom error handler if provided, otherwise use toast
        if (payload.onError) {
          payload.onError(error);
        } else {
          const errorMessage = getErrorMessage(error, payload.type);
          showToast(errorMessage, "error");
        }

        throw error;
      }
    },
    [
      state.nodes,
      state.rootIds,
      state.selectedIds,
      state.activeFolderId,
      caseId,
      showToast,
    ],
  );

  // Convenience methods
  const selectNode = useCallback(
    (nodeId: string, mode: "toggle" | "range" | "add" = "toggle") => {
      if (mode === "toggle") {
        if (state.selectedIds.has(nodeId)) {
          dispatch({ type: "DESELECT_NODE", payload: nodeId });
        } else {
          dispatch({ type: "SELECT_NODE", payload: nodeId });
        }
      } else if (mode === "add") {
        dispatch({ type: "SELECT_NODE", payload: nodeId });
      }
    },
    [state.selectedIds],
  );

  const deselectAll = useCallback(() => {
    dispatch({ type: "CLEAR_SELECTION" });
  }, []);

  const expandFolder = useCallback((folderId: string) => {
    dispatch({
      type: "SET_EXPANDED",
      payload: { id: folderId, expanded: true },
    });
  }, []);

  const collapseFolder = useCallback((folderId: string) => {
    dispatch({
      type: "SET_EXPANDED",
      payload: { id: folderId, expanded: false },
    });
  }, []);

  const setActiveFolder = useCallback((folderId: string | null) => {
    dispatch({ type: "SET_ACTIVE_FOLDER", payload: folderId });
  }, []);

  const getNodePath = useCallback(
    (nodeId: string): FileNode[] => {
      const path: FileNode[] = [];
      let currentId: string | null = nodeId;

      while (currentId) {
        const node: FileNode = state.nodes[currentId];
        if (!node) break;
        path.unshift(node);
        currentId = node.parentId;
      }

      return path;
    },
    [state.nodes],
  );

  const getChildrenOfFolder = useCallback(
    (folderId: string | null): FileNode[] => {
      return Object.values(state.nodes).filter(
        (node) => node.parentId === folderId,
      );
    },
    [state.nodes],
  );

  const showContextMenu = useCallback(
    (x: number, y: number, nodeId?: string) => {
      dispatch({
        type: "SHOW_CONTEXT_MENU",
        payload: { x, y, nodeId },
      });
    },
    [],
  );

  const hideContextMenu = useCallback(() => {
    dispatch({ type: "HIDE_CONTEXT_MENU" });
  }, []);

  const value = useMemo<FileManagerContextType>(
    () => ({
      state,
      performAction,
      selectNode,
      deselectAll,
      expandFolder,
      collapseFolder,
      setActiveFolder,
      getNodePath,
      getChildrenOfFolder,
      showContextMenu,
      hideContextMenu,
    }),
    [
      state,
      performAction,
      selectNode,
      deselectAll,
      expandFolder,
      collapseFolder,
      setActiveFolder,
      getNodePath,
      getChildrenOfFolder,
      showContextMenu,
      hideContextMenu,
    ],
  );

  return (
    <FileManagerContext.Provider value={value}>
      {children}
    </FileManagerContext.Provider>
  );
}

export function useFileManager(): FileManagerContextType {
  const context = useContext(FileManagerContext);
  if (!context) {
    throw new Error("useFileManager must be used within FileManagerProvider");
  }
  return context;
}
